import { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getLocations } from "../features/locations/locationApiSlice";
import { fetchTenantsUsers } from "../features/userManagement/userApiSlice";

/**
 * Returns locations filtered by user role:
 * - admin / operator        → all tenant locations
 * - viewer (global)         → all tenant locations  (assign_locations = [])
 * - viewer (assigned)       → only assigned locations (assign_locations = [1,2,3])
 */
const useFilteredLocations = () => {
  const dispatch = useDispatch();
  const { locations } = useSelector((state) => state.locationApi);

  const tenantId = localStorage.getItem("tenant_id");
  const userRole = localStorage.getItem("user_role");
  const userId   = localStorage.getItem("user_id");

  const isViewer = userRole === "viewer";

  // null = not yet fetched, [] = global (all), [1,2,3] = specific
  const [assignedLocationIds, setAssignedLocationIds] = useState(null);

  useEffect(() => {
    if (tenantId) dispatch(getLocations({ tenantId }));
  }, [dispatch, tenantId]);

  useEffect(() => {
    if (!isViewer || !userId || !tenantId) return;
    dispatch(fetchTenantsUsers({ tenant_id: tenantId, user_id: userId }))
      .unwrap()
      .then((usersData) => {
        const currentUser = usersData.find((u) => String(u.id) === String(userId));
        setAssignedLocationIds(currentUser?.meta?.assign_locations || []);
      })
      .catch(() => setAssignedLocationIds([]));
  }, [dispatch, isViewer, userId, tenantId]);

  const filteredLocations = useMemo(() => {
    // Non-viewer roles → all locations
    if (!isViewer) return locations;

    // Viewer but not yet fetched → show nothing until ready
    if (assignedLocationIds === null) return [];

    // Viewer with empty assign_locations → Global → all locations
    if (assignedLocationIds.length === 0) return locations;

    // Viewer with specific locations → filter (compare as strings to handle number/string mix)
    return locations.filter((loc) =>
      assignedLocationIds.some((id) => String(id) === String(loc.id))
    );
  }, [locations, isViewer, assignedLocationIds]);

  return filteredLocations;
};

export default useFilteredLocations;
